import { useEffect, useRef, useState } from "react";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import type { PDFDocumentProxy } from "pdfjs-dist";

// pdf.js evaluerer DOMMatrix m.m. ved modul-last (krasjer i jsdom/test). Last
// den derfor lazy via dynamic import inne i effekten, så bare type-importen +
// worker-URL-en er statisk.

/**
 * VIB-1033: rendrer en PDF til canvas med pdf.js, så zoom faktisk forstørrer
 * dokumentet skarpt (re-render ved scale), i motsetning til Chromes innebygde
 * iframe-PDF-viewer som ikke kan styres. Ved feil (worker/parse) faller vi
 * tilbake til en native iframe så dokumentet uansett vises.
 */
export function PdfCanvasViewer({ blobUrl, blob, zoom }: { blobUrl: string; blob: Blob; zoom: number }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [failed, setFailed] = useState(false);

  // Last PDF-dokumentet fra blob-en.
  useEffect(() => {
    let cancelled = false;
    let doc: PDFDocumentProxy | null = null;
    setFailed(false);
    setPdf(null);
    (async () => {
      try {
        const pdfjs = await import("pdfjs-dist");
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
        const data = new Uint8Array(await blob.arrayBuffer());
        doc = await pdfjs.getDocument({ data }).promise;
        if (cancelled) {
          doc.destroy();
          return;
        }
        setPdf(doc);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      doc?.destroy();
    };
  }, [blob]);

  // Render alle sider til canvas ved (fit-bredde × zoom).
  useEffect(() => {
    const container = containerRef.current;
    if (!pdf || !container) return;
    let cancelled = false;
    (async () => {
      try {
        const baseWidth = Math.max(120, container.clientWidth - 16);
        const dpr = window.devicePixelRatio || 1;
        const canvases: HTMLCanvasElement[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const unscaled = page.getViewport({ scale: 1 });
          const fitScale = baseWidth / unscaled.width;
          const viewport = page.getViewport({ scale: fitScale * (zoom / 100) * dpr });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = `${viewport.width / dpr}px`;
          canvas.style.height = `${viewport.height / dpr}px`;
          canvas.className = "mx-auto mb-3 bg-white shadow-sm";
          const ctx = canvas.getContext("2d");
          if (!ctx) continue;
          await page.render({ canvas, canvasContext: ctx, viewport }).promise;
          if (cancelled) return;
          canvases.push(canvas);
        }
        if (cancelled) return;
        container.replaceChildren(...canvases);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdf, zoom]);

  if (failed) {
    // Fallback: native iframe (uten skarp zoom, men dokumentet vises).
    return (
      <iframe
        src={`${blobUrl}#view=FitH`}
        className="w-full h-full block border-0"
        title="Bilagsdokument"
      />
    );
  }

  return <div ref={containerRef} className="p-2" />;
}
